import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    CreditCard,
    Sparkles,
    Users,
    Layers,
    Check,
    X,
    Calendar,
    Shield,
    Loader2,
    DollarSign,
    AlertTriangle,
    ArrowUpCircle,
    CheckCircle2,
    FileText,
    Webhook,
    Crown,
    ChevronRight,
    Settings,
    ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useFx, formatCurrency } from "../contexts/FxContext";
import { api, extractAxiosError } from "../lib/api";
import {
    resolveStripePublishableKey,
    stripeKeyModeMismatchMessage,
} from "../lib/stripePublishableKey";
import { PageShell } from "../components/EmptyStateWrapper";
import { LoadingIndicator } from "../components/ShellContentFrame";
import { SupportContactStrip } from "../components/SupportContactStrip";
import { BillingCheckoutModal } from "../components/BillingCheckoutModal";
import { PILL_BADGE_BASE } from "@/lib/pillBadge";
import { DEFAULT_CURRENCY } from "@/lib/defaultCurrency";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PLAN_LABELS = {
    free: "Free",
    pro: "Pro",
    business: "Business",
};

/** Subscription summary card — paid tiers shown in full caps */
function summaryPlanLabel(plan) {
    const label = PLAN_LABELS[plan] || plan;
    if (plan === "pro" || plan === "business") {
        return label.toUpperCase();
    }
    return label;
}

const PLAN_DESCRIPTIONS = {
    free: "One pool, small team — core splits and basic approvals",
    pro: "Unlimited scale, integrations, and a rolling audit trail",
    business: "Unlimited audit history, delegation, priority processing, and org reporting",
};

const PLAN_HIGHLIGHTS = {
    free: [
        "One pool with up to 3 pool members",
        "Co-owner approval on sensitive pool changes",
        "Core splits, invites, and payout workflows",
    ],
    pro: [
        "Unlimited pools and pool members",
        "Audit log — 50 events per rolling week",
        "Slack, Discord, CSV export, and advanced analytics",
    ],
    business: [
        "Unlimited audit log — full compliance history",
        "Delegate admin roles without sharing the owner login",
        "Priority payout processing and advanced reporting",
    ],
};

const PLAN_COMPARE_TIERS = [
    {
        id: "free",
        pitch: "Start with one pool and a small team",
        priceNote: "No subscription",
    },
    {
        id: "pro",
        pitch: "Scale pools, integrations, and weekly audit history",
        priceNote: null,
    },
    {
        id: "business",
        pitch: "Full audit trail, delegation, and priority processing",
        priceNote: null,
    },
];

const BUSINESS_VALUE_POINTS = [
    "Unlimited audit log — every payout and permission change, forever",
    "Delegate admin roles without sharing the owner password",
    "Priority processing when payouts are queued",
    "Advanced reporting across pools and members",
    "Built for multi-workspace operators (coming soon)",
];

const STATUS_LABELS = {
    active: "Active",
    past_due: "Past due",
    canceled: "Canceled",
};

const FEATURE_CATALOG = [
    { key: "basic_pools", label: "Pool creation & management", tier: "free" },
    { key: "basic_revenue_tracking", label: "Basic revenue tracking", tier: "free" },
    { key: "standard_approval", label: "Co-owner approval on sensitive changes", tier: "free" },
    { key: "audit_log_none", label: "Audit log (upgrade to Pro)", tier: "free", group: "audit" },
    { key: "multi_step_approvals", label: "Approval workflows for payouts & pool edits", tier: "pro" },
    { key: "audit_log_weekly", label: "Audit log (50 events per rolling week)", tier: "pro", group: "audit" },
    { key: "advanced_pool_analytics", label: "Advanced pool analytics", tier: "pro" },
    { key: "integrations", label: "Slack & Discord integrations", tier: "pro" },
    { key: "csv_export", label: "CSV data export", tier: "pro" },
    { key: "enhanced_metrics", label: "Enhanced dashboard metrics", tier: "pro" },
    { key: "audit_log_unlimited", label: "Unlimited audit log history", tier: "business", group: "audit" },
    { key: "admin_delegation", label: "Delegate admin roles safely", tier: "business" },
    { key: "priority_processing", label: "Priority payout processing", tier: "business" },
    { key: "advanced_reporting", label: "Advanced org reporting", tier: "business" },
    { key: "multi_org_management", label: "Multi-workspace management", tier: "business" },
];

const TIER_ORDER = { free: 0, pro: 1, business: 2 };

const STATIC_PLAN_FEATURES = {
    free: {
        basic_pools: true,
        basic_revenue_tracking: true,
        standard_approval: true,
        audit_log_access: false,
        audit_log_unlimited: false,
        multi_step_approvals: false,
        advanced_pool_analytics: false,
        integrations: false,
        csv_export: false,
        enhanced_metrics: false,
        admin_delegation: false,
        priority_processing: false,
        advanced_reporting: false,
        multi_org_management: false,
    },
    pro: {
        basic_pools: true,
        basic_revenue_tracking: true,
        standard_approval: true,
        audit_log_access: true,
        audit_log_unlimited: false,
        multi_step_approvals: true,
        advanced_pool_analytics: true,
        integrations: true,
        csv_export: true,
        enhanced_metrics: true,
        admin_delegation: false,
        priority_processing: false,
        advanced_reporting: false,
        multi_org_management: false,
    },
    business: {
        basic_pools: true,
        basic_revenue_tracking: true,
        standard_approval: true,
        audit_log_access: true,
        audit_log_unlimited: true,
        multi_step_approvals: true,
        advanced_pool_analytics: true,
        integrations: true,
        csv_export: true,
        enhanced_metrics: true,
        admin_delegation: true,
        priority_processing: true,
        advanced_reporting: true,
        multi_org_management: true,
    },
};

function statusBadgeClass(status) {
    switch (status) {
        case "active":
            return "bg-[#00BF0D]/10 border-[#00BF0D]/25 text-[#00BF0D]";
        case "past_due":
            return "bg-amber-500/10 border-amber-500/25 text-amber-300";
        case "canceled":
            return "bg-rose-500/10 border-rose-500/25 text-rose-300";
        default:
            return "bg-white/[0.04] border-white/10 text-white/55";
    }
}

function poolLimitForPlan(plan) {
    return plan === "free" ? 1 : null;
}

function usagePercent(current, limit) {
    if (limit == null || limit <= 0) return null;
    return Math.min(100, Math.round((current / limit) * 100));
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function confirmCheckoutWithRetry(orgId, sessionId, attempts = 6) {
    let lastError = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
            const { data } = await api.post("/billing/checkout/confirm", {
                org_id: orgId,
                session_id: sessionId,
            });
            if (data?.confirmed) {
                return data;
            }
        } catch (e) {
            lastError = e;
        }
        if (attempt < attempts - 1) {
            await sleep(1500);
        }
    }
    if (lastError) {
        throw lastError;
    }
    return null;
}

async function syncSubscriptionWithRetry(orgId, attempts = 5, delayMs = 1500) {
    let lastError = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
            const { data } = await api.post("/billing/subscription/sync", { org_id: orgId });
            if (data?.synced) {
                return data;
            }
        } catch (e) {
            lastError = e;
        }
        if (attempt < attempts - 1) {
            await sleep(delayMs);
        }
    }
    if (lastError) {
        throw lastError;
    }
    return null;
}

function formatBillingDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch (_e) {
        return null;
    }
}

function formatActivityTimestamp(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch (_e) {
        return iso;
    }
}

function isBillingActivityAuditAction(action) {
    return typeof action === "string" && action.startsWith("billing.");
}

function humanizeBillingAction(action) {
    return action
        .replace(/^billing\./, "")
        .split(/[._]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

function auditLogToBillingActivityEvent(log) {
    if (!log?.created_at || !isBillingActivityAuditAction(log.action)) {
        return null;
    }

    const metadata = log.metadata || {};
    const targetPlan = metadata.plan ? (PLAN_LABELS[metadata.plan] || metadata.plan) : null;

    switch (log.action) {
        case "billing.checkout_created":
            return {
                id: `audit-${log.id}`,
                at: log.created_at,
                label: targetPlan ? `Subscription checkout started` : "Subscription checkout started",
                detail: targetPlan
                    ? `Upgrade to ${targetPlan}${metadata.session_id ? ` · session ${String(metadata.session_id).slice(-10)}` : ""}`
                    : metadata.session_id
                        ? `Session ${String(metadata.session_id).slice(-10)}`
                        : null,
                tone: "info",
                icon: CreditCard,
            };
        case "billing.subscription_upgraded":
        case "billing.plan_upgraded":
            return {
                id: `audit-${log.id}`,
                at: log.created_at,
                label: metadata.from_plan && metadata.to_plan
                    ? `Subscription upgraded (${PLAN_LABELS[metadata.from_plan] || metadata.from_plan} → ${PLAN_LABELS[metadata.to_plan] || metadata.to_plan})`
                    : targetPlan
                        ? `Subscription upgraded to ${targetPlan}`
                        : "Subscription upgraded",
                detail: metadata.session_id ? `Session ${String(metadata.session_id).slice(-10)}` : null,
                tone: "success",
                icon: ArrowUpCircle,
            };
        case "billing.payment_succeeded":
        case "billing.payment_successful":
            return {
                id: `audit-${log.id}`,
                at: log.created_at,
                label: "Payment successful",
                detail: targetPlan ? `${targetPlan} subscription` : null,
                tone: "success",
                icon: CheckCircle2,
            };
        case "billing.invoice_created":
        case "billing.invoice_generated":
            return {
                id: `audit-${log.id}`,
                at: log.created_at,
                label: "Invoice generated",
                detail: metadata.invoice_id ? String(metadata.invoice_id) : targetPlan ? `${targetPlan} plan` : null,
                tone: "neutral",
                icon: FileText,
            };
        case "billing.webhook_processed":
            return {
                id: `audit-${log.id}`,
                at: log.created_at,
                label: "Webhook processed",
                detail: metadata.type ? String(metadata.type) : metadata.event_id ? String(metadata.event_id) : null,
                tone: "neutral",
                icon: Webhook,
            };
        default:
            return {
                id: `audit-${log.id}`,
                at: log.created_at,
                label: humanizeBillingAction(log.action),
                detail: targetPlan ? `${targetPlan} plan` : null,
                tone: "neutral",
                icon: CreditCard,
            };
    }
}

function subscriptionPayloadToActivityEvents(billing) {
    if (!billing || typeof billing !== "object") return [];

    const candidates = [billing.activity, billing.events, billing.history].filter(Array.isArray);
    const events = [];

    for (const list of candidates) {
        for (const item of list) {
            if (!item || typeof item !== "object") continue;

            const at = item.at || item.created_at || item.occurred_at || item.timestamp || item.processed_at;
            if (!at) continue;

            const label = item.label || item.title || item.message;
            if (!label) continue;

            events.push({
                id: item.id ? `subscription-${item.id}` : `subscription-${at}-${label}`,
                at,
                label: String(label),
                detail: item.detail || item.description || null,
                tone: item.tone || "neutral",
                icon: CreditCard,
            });
        }
    }

    return events;
}

function buildBillingActivityEvents(auditLogs, billing) {
    const byId = new Map();

    for (const log of auditLogs || []) {
        const event = auditLogToBillingActivityEvent(log);
        if (event) byId.set(event.id, event);
    }

    for (const event of subscriptionPayloadToActivityEvents(billing)) {
        if (!byId.has(event.id)) byId.set(event.id, event);
    }

    return Array.from(byId.values()).sort(
        (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
}

function activityToneClasses(tone) {
    switch (tone) {
        case "success":
            return {
                dot: "border-[#00BF0D]/50 bg-[#00BF0D]/20",
                icon: "text-[#00BF0D]",
            };
        case "warning":
            return {
                dot: "border-amber-400/50 bg-amber-400/15",
                icon: "text-amber-300",
            };
        case "info":
            return {
                dot: "border-[#00D4FF]/50 bg-[#00D4FF]/15",
                icon: "text-[#00D4FF]",
            };
        default:
            return {
                dot: "border-white/20 bg-white/[0.06]",
                icon: "text-white/45",
            };
    }
}

function BillingActivityTimeline({ events }) {
    if (!events.length) {
        return (
            <p className="text-xs text-white/45 leading-relaxed" data-testid="billing-activity-empty">
                No billing activity recorded yet. Checkout starts and Stripe subscription events will appear here when logged.
            </p>
        );
    }

    return (
        <ul className="space-y-0" data-testid="billing-activity-timeline">
            {events.map((event, index) => {
                const Icon = event.icon || CreditCard;
                const tone = activityToneClasses(event.tone);
                const isLast = index === events.length - 1;

                return (
                    <li
                        key={event.id}
                        className="relative flex gap-3 pb-3 last:pb-0"
                        data-testid={`billing-activity-item-${event.id}`}
                    >
                        {!isLast && (
                            <span
                                className="absolute left-[6px] top-4 bottom-0 w-px bg-white/[0.08]"
                                aria-hidden
                            />
                        )}
                        <span
                            className={`relative z-10 mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${tone.dot}`}
                            aria-hidden
                        >
                            <span className="h-1 w-1 rounded-full bg-current opacity-80" />
                        </span>
                        <div className="min-w-0 flex-1 pt-px">
                            <div className="flex items-start gap-2">
                                <Icon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${tone.icon}`} aria-hidden />
                                <div className="min-w-0">
                                    <p className="text-xs font-medium text-white/85 leading-snug">{event.label}</p>
                                    {event.detail && (
                                        <p className="text-[11px] text-white/40 mt-0.5 leading-snug truncate">
                                            {event.detail}
                                        </p>
                                    )}
                                    <time
                                        className="text-[10px] text-white/30 tabular-nums mt-1 block"
                                        dateTime={event.at}
                                    >
                                        {formatActivityTimestamp(event.at)}
                                    </time>
                                </div>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function formatMonthlyAmount(amount, currency = DEFAULT_CURRENCY) {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency.toUpperCase(),
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch (_e) {
        return `$${amount}`;
    }
}

/** Uses subscription API fields only — no hardcoded paid plan prices. */
function resolveMonthlyPriceLabel(billing, displayPlan) {
    if (displayPlan === "free") {
        return formatMonthlyAmount(0);
    }

    if (!billing || typeof billing !== "object") {
        return null;
    }

    const planPrice = billing.plan_prices?.[displayPlan];
    if (planPrice?.unit_amount != null) {
        return formatMonthlyAmount(
            Number(planPrice.unit_amount) / 100,
            planPrice.currency || DEFAULT_CURRENCY,
        );
    }

    const subscriptionPrice = billing.subscription_price;
    if (subscriptionPrice?.unit_amount != null) {
        return formatMonthlyAmount(
            Number(subscriptionPrice.unit_amount) / 100,
            subscriptionPrice.currency || DEFAULT_CURRENCY,
        );
    }

    const currency = billing.currency || billing.price_currency || DEFAULT_CURRENCY;

    if (billing.monthly_price != null) {
        return formatMonthlyAmount(Number(billing.monthly_price), currency);
    }
    if (billing.price != null && typeof billing.price !== "object") {
        return formatMonthlyAmount(Number(billing.price), currency);
    }
    if (billing.plan_price != null) {
        return formatMonthlyAmount(Number(billing.plan_price), currency);
    }
    if (billing.unit_amount != null) {
        const cents = Number(billing.unit_amount);
        return formatMonthlyAmount(Number.isFinite(cents) ? cents / 100 : billing.unit_amount, currency);
    }
    if (billing.price?.unit_amount != null) {
        const cents = Number(billing.price.unit_amount);
        const priceCurrency = billing.price.currency || currency;
        return formatMonthlyAmount(Number.isFinite(cents) ? cents / 100 : billing.price.unit_amount, priceCurrency);
    }

    return null;
}

function SummaryMetric({ label, value, icon: Icon, testId }) {
    return (
        <div className="min-w-0 rounded-lg border border-white/[0.06] bg-black/20 px-3 py-2" data-testid={testId}>
            <dt className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden />}
                {label}
            </dt>
            <dd className="mt-1 text-sm font-semibold text-white/90 tabular-nums truncate">{value}</dd>
        </div>
    );
}

function SubscriptionManageAction({
    cancelAtPeriodEnd,
    canceling,
    resuming,
    onCancelSubscription,
    onResumeSubscription,
}) {
    if (cancelAtPeriodEnd) {
        return (
            <button
                type="button"
                onClick={onResumeSubscription}
                disabled={resuming}
                className="text-[11px] text-white/45 transition-colors hover:text-[#00BF0D] disabled:opacity-50"
                data-testid="billing-resume-subscription-btn"
            >
                {resuming ? "Updating…" : "Keep subscription"}
            </button>
        );
    }

    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <button
                    type="button"
                    disabled={canceling}
                    className="text-[11px] text-white/45 transition-colors hover:text-white/70 disabled:opacity-50"
                    data-testid="billing-cancel-subscription-btn"
                >
                    {canceling ? "Canceling…" : "Cancel subscription"}
                </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="border-white/10 bg-[#111113] text-white">
                <AlertDialogHeader>
                    <AlertDialogTitle>Cancel subscription?</AlertDialogTitle>
                    <AlertDialogDescription className="text-white/55">
                        Your plan stays active until the end of the current billing period. You will not be charged again, and your workspace will move to Free after that.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-white/5">
                        Keep plan
                    </AlertDialogCancel>
                    <AlertDialogAction
                        className="bg-white/10 text-white hover:bg-white/15"
                        onClick={(event) => {
                            event.preventDefault();
                            onCancelSubscription();
                        }}
                    >
                        Cancel at period end
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

function SubscriptionSummaryCard({
    displayPlan,
    effectivePlan,
    subscriptionStatus,
    renewalDate,
    monthlyPriceLabel,
    stripeSubscriptionId,
    cancelAtPeriodEnd,
    canManageBilling,
    canceling,
    resuming,
    onCancelSubscription,
    onResumeSubscription,
}) {
    const priceDisplay = monthlyPriceLabel != null ? `${monthlyPriceLabel}/mo` : "—";
    const isPaidPlan = displayPlan === "pro" || displayPlan === "business";
    const showCancelActions = isPaidPlan && stripeSubscriptionId && canManageBilling;
    const statusLabel = cancelAtPeriodEnd
        ? "Active until period end"
        : (STATUS_LABELS[subscriptionStatus] || subscriptionStatus);

    return (
        <section
            className="relative overflow-hidden rounded-xl border border-white/[0.10] bg-gradient-to-br from-white/[0.07] via-white/[0.03] to-transparent p-4 sm:p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
            data-testid="billing-subscription-summary"
        >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    <p className="label-eyebrow mb-2">Subscription summary</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 fs-pill-anchor">
                        <h2
                            className="font-heading text-2xl sm:text-[1.75rem] font-semibold tracking-tight text-white"
                            data-testid="billing-plan-badge"
                        >
                            {summaryPlanLabel(displayPlan)}
                        </h2>
                        <span
                            className={`${PILL_BADGE_BASE} px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusBadgeClass(cancelAtPeriodEnd ? "active" : subscriptionStatus)}`}
                            data-testid="billing-status-badge"
                        >
                            {cancelAtPeriodEnd ? "Ending" : (STATUS_LABELS[subscriptionStatus] || subscriptionStatus)}
                        </span>
                    </div>
                    {effectivePlan !== displayPlan && (
                        <p className="text-[11px] text-white/40 mt-1.5">
                            Effective access: {summaryPlanLabel(effectivePlan)}
                        </p>
                    )}
                    {stripeSubscriptionId && (
                        <details className="mt-2.5 group">
                            <summary className="cursor-pointer text-[10px] font-medium uppercase tracking-wider text-white/35 list-none hover:text-white/50 [&::-webkit-details-marker]:hidden">
                                <span className="inline-flex items-center gap-1">
                                    Stripe subscription ID
                                    <span className="text-white/25 group-open:rotate-90 transition-transform">›</span>
                                </span>
                            </summary>
                            <p
                                className="mt-1.5 text-[10px] font-mono text-white/30 truncate max-w-full"
                                data-testid="billing-summary-subscription-id"
                            >
                                {stripeSubscriptionId}
                            </p>
                        </details>
                    )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3 lg:min-w-[min(100%,22rem)] lg:shrink-0">
                    <SummaryMetric
                        label={cancelAtPeriodEnd ? "Access until" : "Renews"}
                        value={renewalDate || "—"}
                        icon={Calendar}
                        testId="billing-summary-renewal"
                    />
                    <SummaryMetric
                        label="Monthly"
                        value={priceDisplay}
                        icon={DollarSign}
                        testId="billing-summary-price"
                    />
                    <SummaryMetric
                        label="Status"
                        value={statusLabel}
                        icon={Shield}
                        testId="billing-summary-status-metric"
                    />
                    {showCancelActions && (
                        <div className="col-span-2 sm:col-start-2 sm:col-span-2 px-1 pt-1 border-t border-white/[0.05]">
                            <SubscriptionManageAction
                                cancelAtPeriodEnd={cancelAtPeriodEnd}
                                canceling={canceling}
                                resuming={resuming}
                                onCancelSubscription={onCancelSubscription}
                                onResumeSubscription={onResumeSubscription}
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function resolveFeatureFlags(billing, effectivePlan) {
    if (billing?.features && typeof billing.features === "object") {
        return billing.features;
    }
    return STATIC_PLAN_FEATURES[effectivePlan] || STATIC_PLAN_FEATURES.free;
}

function auditFeatureKeyForPlan(plan) {
    if (plan === "business") return "audit_log_unlimited";
    if (plan === "pro") return "audit_log_weekly";
    return "audit_log_none";
}

function featuresForPlanDisplay(effectivePlan) {
    const tierLevel = TIER_ORDER[effectivePlan] ?? 0;
    const auditKey = auditFeatureKeyForPlan(effectivePlan);
    return FEATURE_CATALOG.filter((f) => {
        if (f.group === "audit") {
            return f.key === auditKey;
        }
        return (TIER_ORDER[f.tier] ?? 0) <= tierLevel;
    });
}

function isFeatureEnabled(featureKey, flags, effectivePlan) {
    if (featureKey === "audit_log_none") {
        return false;
    }
    if (featureKey === "audit_log_weekly") {
        return Boolean(flags.audit_log_access) && !flags.audit_log_unlimited;
    }
    if (featureKey === "audit_log_unlimited") {
        return Boolean(flags.audit_log_unlimited);
    }
    return Boolean(flags[featureKey]);
}

function usageUtilizationState(current, limit) {
    if (limit == null || limit <= 0) return "unlimited";
    const value = typeof current === "number" ? current : 0;
    if (value >= limit) return "at_limit";
    const remaining = limit - value;
    const ratio = value / limit;
    if (remaining <= 1 || ratio >= 0.67) return "near_limit";
    return "ok";
}

function usageUpgradeMessage(state, displayPlan, metricLabel, upgradePlan) {
    if (!upgradePlan || state === "unlimited" || state === "ok") return null;

    const planLabel = PLAN_LABELS[displayPlan] || displayPlan;
    const targetLabel = PLAN_LABELS[upgradePlan] || upgradePlan;
    const metric = metricLabel.toLowerCase();

    if (state === "at_limit") {
        return `${metricLabel} limit reached on ${planLabel} — upgrade to ${targetLabel} to unlock more.`;
    }

    return `You're close to your ${planLabel} ${metric} limit — upgrade to ${targetLabel} for unlimited access.`;
}

function formatUsageLabel(current, limit, unitLabel) {
    const value = typeof current === "number" ? current : 0;
    const unit = unitLabel.toLowerCase();
    if (limit == null) {
        return `${value} ${unit} · unlimited`;
    }
    return `${value} / ${limit} ${unit}`;
}

function UsageMeter({ label, unitLabel, icon: Icon, current, limit, displayPlan, upgradePlan }) {
    const pct = usagePercent(current, limit);
    const state = usageUtilizationState(current, limit);
    const unlimited = state === "unlimited";
    const atLimit = state === "at_limit";
    const nearLimit = state === "near_limit";
    const upgradeMessage = usageUpgradeMessage(state, displayPlan, unitLabel || label, upgradePlan);

    const containerClass = atLimit
        ? "rounded-lg border border-rose-500/30 bg-rose-500/[0.06] p-2.5"
        : nearLimit
            ? "rounded-lg border border-amber-500/25 bg-amber-500/[0.05] p-2.5"
            : "p-0.5";

    const countClass = atLimit
        ? "text-rose-300 font-semibold"
        : nearLimit
            ? "text-amber-300 font-medium"
            : "text-white/45";

    const barTrackClass = atLimit
        ? "bg-rose-500/15"
        : nearLimit
            ? "bg-amber-500/10"
            : "bg-white/[0.06]";

    const barFillClass = atLimit
        ? "bg-rose-400/90"
        : nearLimit
            ? "bg-amber-400/80"
            : unlimited
                ? "bg-[#00BF0D]/20 w-full"
                : "bg-[#00BF0D]/65";

    const hintClass = atLimit
        ? "text-rose-300/90 border-rose-500/20 bg-rose-500/10"
        : "text-amber-300/90 border-amber-500/20 bg-amber-500/10";

    return (
        <div
            className={`space-y-2 ${containerClass}`}
            data-testid={`billing-usage-meter-${(unitLabel || label).toLowerCase()}`}
            data-usage-state={state}
        >
            <div className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2 min-w-0 text-white/70">
                    <Icon className={`h-3.5 w-3.5 shrink-0 ${atLimit ? "text-rose-400/80" : nearLimit ? "text-amber-400/80" : "text-white/40"}`} />
                    <span className="font-medium">{label}</span>
                    {atLimit && (
                        <span className="fs-pill gap-0.5 rounded-full border border-rose-500/30 bg-rose-500/15 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-rose-300">
                            <AlertTriangle className="h-2.5 w-2.5" aria-hidden />
                            Limit
                        </span>
                    )}
                </div>
                <span className={`tabular-nums shrink-0 text-right ${countClass}`}>
                    {formatUsageLabel(current, limit, unitLabel || label)}
                </span>
            </div>
            <div className={`h-1.5 rounded-full overflow-hidden ${barTrackClass}`}>
                <div
                    className={`h-full rounded-full transition-all duration-500 ${barFillClass}`}
                    style={unlimited ? undefined : { width: `${pct ?? 0}%` }}
                    role="progressbar"
                    aria-valuenow={typeof current === "number" ? current : 0}
                    aria-valuemin={0}
                    aria-valuemax={limit ?? undefined}
                    aria-label={`${label} usage`}
                />
            </div>
            {upgradeMessage && (
                <p className={`text-[11px] leading-snug rounded-md border px-2 py-1.5 ${hintClass}`}>
                    {upgradeMessage}
                </p>
            )}
            {unlimited && (
                <p className="text-[10px] text-white/35">No cap on this plan.</p>
            )}
        </div>
    );
}

function FeatureRow({ label, enabled }) {
    return (
        <li className="flex items-center gap-2 py-0.5 text-xs">
            {enabled ? (
                <Check className="h-3.5 w-3.5 shrink-0 text-[#00BF0D]" aria-hidden />
            ) : (
                <X className="h-3.5 w-3.5 shrink-0 text-white/20" aria-hidden />
            )}
            <span className={enabled ? "text-white/75" : "text-white/30"}>{label}</span>
        </li>
    );
}

function IncludedFeaturesSection({ effectivePlan, planFeatureRows }) {
    const enabledCount = planFeatureRows.filter((f) => f.enabled).length;

    return (
        <div
            className="border-t border-white/[0.06] pt-4"
            data-testid="billing-included-features-section"
        >
            <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-white/35">
                        Included features
                    </p>
                    <p className="text-[11px] text-white/45 mt-0.5">
                        {enabledCount} active on {PLAN_LABELS[effectivePlan] || effectivePlan}
                    </p>
                </div>
                {effectivePlan !== "free" && (
                    <Link
                        to="/audit"
                        className="shrink-0 text-[11px] font-medium text-[#00BF0D] hover:text-[#5ee06a]"
                        data-testid="billing-audit-link"
                    >
                        Audit log →
                    </Link>
                )}
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                {planFeatureRows.map((f) => (
                    <FeatureRow key={f.key} label={f.label} enabled={f.enabled} />
                ))}
            </ul>
        </div>
    );
}

function PlanCompareSection({ displayPlan, planPriceLabels }) {
    return (
        <div className="border-t border-white/[0.06] pt-4 mt-4" data-testid="billing-compare-plans-section">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/35 mb-2">
                Compare plans
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {PLAN_COMPARE_TIERS.map((tier) => {
                    const isCurrent = tier.id === displayPlan;
                    const priceLabel = planPriceLabels?.[tier.id];
                    const priceDisplay =
                        tier.id === "free"
                            ? tier.priceNote
                            : priceLabel
                                ? `${priceLabel}/mo`
                                : "—";

                    return (
                        <li
                            key={tier.id}
                            className={`rounded-lg border px-2.5 py-2 transition-colors ${
                                isCurrent
                                    ? "border-[#00BF0D]/35 bg-[#00BF0D]/[0.06]"
                                    : "border-white/[0.06] bg-white/[0.02]"
                            }`}
                            data-testid={`billing-compare-tier-${tier.id}`}
                            data-current={isCurrent ? "true" : undefined}
                        >
                            <div className="flex items-center justify-between gap-1">
                                <span
                                    className={`text-[11px] font-semibold ${
                                        isCurrent ? "text-[#00BF0D]" : "text-white/70"
                                    }`}
                                >
                                    {summaryPlanLabel(tier.id)}
                                </span>
                                {isCurrent && (
                                    <span className="fs-pill rounded border border-[#00BF0D]/30 bg-[#00BF0D]/10 px-1 py-px text-[8px] font-semibold uppercase text-[#00BF0D]">
                                        Now
                                    </span>
                                )}
                            </div>
                            <p className="text-[10px] tabular-nums text-white/40 mt-0.5">{priceDisplay}</p>
                            <p className="text-[10px] text-white/45 leading-snug mt-1 line-clamp-2">{tier.pitch}</p>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function BillingQuickLinks({ effectivePlan }) {
    const links = [
        { to: "/pools", label: "Pools", icon: Layers, testId: "billing-link-pools" },
        { to: "/settings", label: "Settings", icon: Settings, testId: "billing-link-settings" },
    ];
    if (effectivePlan !== "free") {
        links.splice(1, 0, { to: "/audit", label: "Audit log", icon: FileText, testId: "billing-link-audit" });
    }

    return (
        <div className="border-t border-white/[0.06] pt-4" data-testid="billing-quick-links">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/35 mb-2">
                Workspace
            </p>
            <div className="grid grid-cols-2 gap-2">
                {links.map(({ to, label, icon: Icon, testId }) => (
                    <Link
                        key={to}
                        to={to}
                        className="flex items-center justify-between gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-2 text-xs text-white/70 transition-colors hover:border-white/10 hover:bg-white/[0.04] hover:text-white"
                        data-testid={testId}
                    >
                        <span className="flex items-center gap-1.5 min-w-0">
                            <Icon className="h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden />
                            <span className="truncate">{label}</span>
                        </span>
                        <ArrowRight className="h-3 w-3 shrink-0 text-white/30" aria-hidden />
                    </Link>
                ))}
            </div>
        </div>
    );
}

function BillingRecentActivityPreview({ events }) {
    const recent = events.slice(0, 4);
    const hasMore = events.length > recent.length;

    return (
        <div className="border-t border-white/[0.06] pt-4" data-testid="billing-recent-activity-preview">
            <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-white/35">
                    Recent billing activity
                </p>
                {hasMore && (
                    <a
                        href="#billing-activity-full"
                        className="text-[11px] font-medium text-[#00BF0D] hover:text-[#5ee06a]"
                    >
                        View all
                    </a>
                )}
            </div>
            {recent.length === 0 ? (
                <p className="text-xs text-white/45 leading-relaxed">
                    Checkout and subscription events will show here once logged.
                </p>
            ) : (
                <ul className="space-y-2">
                    {recent.map((event) => {
                        const Icon = event.icon || CreditCard;
                        const tone = activityToneClasses(event.tone);
                        return (
                            <li
                                key={event.id}
                                className="flex items-start gap-2 rounded-md border border-white/[0.05] bg-white/[0.02] px-2.5 py-2"
                            >
                                <Icon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${tone.icon}`} aria-hidden />
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-white/80 leading-snug truncate">
                                        {event.label}
                                    </p>
                                    <time
                                        className="text-[10px] text-white/35 tabular-nums"
                                        dateTime={event.at}
                                    >
                                        {formatActivityTimestamp(event.at)}
                                    </time>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function BillingCard({ title, subtitle, children, testId, className = "", id }) {
    return (
        <section id={id} className={`surface p-4 ${className}`} data-testid={testId}>
            {(title || subtitle) && (
                <header className="mb-3">
                    {title && <h2 className="label-eyebrow">{title}</h2>}
                    {subtitle && <p className="text-[11px] text-white/40 mt-1">{subtitle}</p>}
                </header>
            )}
            {children}
        </section>
    );
}

function formatUpgradePlanPrice(planPrices, plan, displayCurrency, convert) {
    const entry = planPrices?.[plan];
    if (entry?.unit_amount == null) return null;

    const amount = Number(entry.unit_amount) / 100;
    const fromCurrency = (entry.currency || DEFAULT_CURRENCY).toUpperCase();
    const toCurrency = (displayCurrency || DEFAULT_CURRENCY).toUpperCase();
    const displayAmount = fromCurrency === toCurrency
        ? amount
        : convert(amount, fromCurrency, toCurrency);

    return formatCurrency(displayAmount, toCurrency, { decimals: 0 });
}

function UpgradePlanButton({ plan, label, recommended, busy, onUpgrade, testId, accent = "pro", priceLabel }) {
    const isBusiness = accent === "business";
    const variant = isBusiness ? "business" : recommended ? "pro-featured" : "pro";

    const LeadingIcon = isBusiness ? Crown : recommended ? Sparkles : CreditCard;

    return (
        <button
            type="button"
            disabled={busy}
            onClick={() => onUpgrade(plan)}
            className={`billing-upgrade-btn billing-upgrade-btn--${variant}${busy ? " is-busy" : ""}`}
            data-testid={testId}
            data-recommended={recommended ? "true" : undefined}
            data-accent={isBusiness ? "business" : undefined}
        >
            <span className="billing-upgrade-btn__shine" aria-hidden="true" />
            <span className="billing-upgrade-btn__inner">
                <span className={`billing-upgrade-btn__icon-wrap billing-upgrade-btn__icon-wrap--${variant}`} aria-hidden="true">
                    <LeadingIcon className="billing-upgrade-btn__icon" />
                </span>
                <span className="billing-upgrade-btn__copy">
                    <span className="billing-upgrade-btn__title-row">
                        <span className="billing-upgrade-btn__title">{label}</span>
                        {recommended && (
                            <span className="billing-upgrade-btn__chip billing-upgrade-btn__chip--pro">Recommended</span>
                        )}
                        {isBusiness && (
                            <span className="billing-upgrade-btn__chip billing-upgrade-btn__chip--gold">Premium</span>
                        )}
                    </span>
                    {priceLabel && (
                        <span className="billing-upgrade-btn__price-hint" data-testid={`${testId}-price`}>
                            {priceLabel}
                            <span className="billing-upgrade-btn__price-period">/mo</span>
                        </span>
                    )}
                </span>
                <span className="billing-upgrade-btn__trailing">
                    {busy ? (
                        <Loader2 className="billing-upgrade-btn__action-icon animate-spin" aria-hidden="true" />
                    ) : (
                        <ChevronRight className="billing-upgrade-btn__action-icon" aria-hidden="true" />
                    )}
                </span>
            </span>
        </button>
    );
}

function UpgradeBenefitList({ items }) {
    return (
        <ul className="space-y-1.5 mb-4" data-testid="billing-upgrade-benefits">
            {items.map((text) => (
                <li key={text} className="flex items-center gap-2 text-xs text-white/65">
                    <Check className="h-3.5 w-3.5 shrink-0 text-[#00BF0D]" aria-hidden />
                    <span>{text}</span>
                </li>
            ))}
        </ul>
    );
}

function UpgradePanel({
    showUpgradeFree,
    showUpgradeBusiness,
    checkoutPlan,
    onUpgrade,
    planPriceLabels,
    onSyncFromStripe,
    syncingPlan,
}) {
    const benefits = showUpgradeFree
        ? [
            "Unlimited pools & members on Pro",
            "Audit log (50 events/week) on Pro",
            "Slack, Discord, CSV export on Pro",
        ]
        : BUSINESS_VALUE_POINTS;

    return (
        <BillingCard
            testId="billing-upgrade-card"
            className={
                showUpgradeFree
                    ? "border border-[#00BF0D]/20 bg-gradient-to-b from-[#00BF0D]/[0.05] to-transparent"
                    : ""
            }
        >
            <div className="flex items-start justify-between gap-3 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                    <Sparkles className="h-4 w-4 text-[#00BF0D] shrink-0" aria-hidden />
                    <h2 className="font-heading text-base font-semibold tracking-tight">Upgrade your plan</h2>
                </div>
                <button
                    type="button"
                    onClick={onSyncFromStripe}
                    disabled={syncingPlan}
                    className="shrink-0 text-[11px] text-white/45 hover:text-white/70 disabled:opacity-50"
                    data-testid="billing-sync-stripe"
                >
                    {syncingPlan ? "Refreshing…" : "Refresh from Stripe"}
                </button>
            </div>
            <p className="text-[11px] text-white/45 mb-3">
                {showUpgradeFree
                    ? "You're on Free — unlock more capacity, audit history, and integrations with Pro."
                    : "You're on Pro — Business adds unlimited compliance history, delegation, and priority processing."}
            </p>

            <UpgradeBenefitList items={benefits} />

            <div className="billing-upgrade-actions space-y-2.5">
                {showUpgradeFree && (
                    <>
                        <UpgradePlanButton
                            plan="pro"
                            label="Upgrade to Pro"
                            recommended
                            busy={checkoutPlan === "pro"}
                            onUpgrade={onUpgrade}
                            testId="billing-upgrade-pro-btn"
                            priceLabel={planPriceLabels?.pro}
                        />
                        <UpgradePlanButton
                            plan="business"
                            label="Upgrade to Business"
                            busy={checkoutPlan === "business"}
                            onUpgrade={onUpgrade}
                            testId="billing-upgrade-business-btn"
                            accent="business"
                            priceLabel={planPriceLabels?.business}
                        />
                    </>
                )}
                {showUpgradeBusiness && (
                    <UpgradePlanButton
                        plan="business"
                        label="Upgrade to Business"
                        busy={checkoutPlan === "business"}
                        onUpgrade={onUpgrade}
                        testId="billing-upgrade-business-btn"
                        accent="business"
                        priceLabel={planPriceLabels?.business}
                    />
                )}
            </div>

            <p className="text-[10px] text-white/35 mt-3">
                Secure in-app checkout — powered by Stripe.
            </p>
        </BillingCard>
    );
}

function BillingDashboard({
    billing,
    displayPlan,
    effectivePlan,
    subscriptionStatus,
    memberCount,
    poolMemberLimit,
    poolCount,
    poolLimit,
    nextBillingDate,
    monthlyPriceLabel,
    planFeatureRows,
    showUpgradeFree,
    showUpgradeBusiness,
    checkoutPlan,
    onUpgrade,
    activityEvents,
    planPriceLabels,
    onSyncFromStripe,
    syncingPlan,
    cancelAtPeriodEnd,
    canManageBilling,
    canceling,
    resuming,
    onCancelSubscription,
    onResumeSubscription,
}) {
    const billingAlerts = (
        <>
            {subscriptionStatus === "past_due" && (
                <p className="text-[11px] text-amber-300/90 rounded-md border border-amber-500/20 bg-amber-500/10 px-2.5 py-1.5">
                    Payment past due — update billing in Stripe.
                </p>
            )}
            {subscriptionStatus === "canceled" && displayPlan !== "free" && (
                <p className="text-[11px] text-rose-300/90 rounded-md border border-rose-500/20 bg-rose-500/10 px-2.5 py-1.5">
                    Subscription canceled — upgrade to restore paid features.
                </p>
            )}
        </>
    );

    const upgradeTargetPlan = showUpgradeFree ? "pro" : showUpgradeBusiness ? "business" : null;
    const usageHasPressure =
        usageUtilizationState(memberCount, poolMemberLimit) !== "ok" ||
        usageUtilizationState(poolCount, poolLimit) !== "ok";

    return (
        <div className="space-y-4 lg:space-y-5">
            <SubscriptionSummaryCard
                displayPlan={displayPlan}
                effectivePlan={effectivePlan}
                subscriptionStatus={subscriptionStatus}
                renewalDate={nextBillingDate}
                monthlyPriceLabel={monthlyPriceLabel}
                stripeSubscriptionId={billing.stripe_subscription_id}
                cancelAtPeriodEnd={cancelAtPeriodEnd}
                canManageBilling={canManageBilling}
                canceling={canceling}
                resuming={resuming}
                onCancelSubscription={onCancelSubscription}
                onResumeSubscription={onResumeSubscription}
            />

            {(showUpgradeFree || showUpgradeBusiness) && (
                <UpgradePanel
                    showUpgradeFree={showUpgradeFree}
                    showUpgradeBusiness={showUpgradeBusiness}
                    checkoutPlan={checkoutPlan}
                    onUpgrade={onUpgrade}
                    planPriceLabels={planPriceLabels}
                    onSyncFromStripe={onSyncFromStripe}
                    syncingPlan={syncingPlan}
                />
            )}

            <div
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 lg:items-stretch"
                data-testid="billing-dashboard-grid"
            >
                <BillingCard title="Plan details" testId="billing-plan-card" className="lg:h-full">
                    <p className="text-sm text-white/55 leading-snug">
                        {PLAN_DESCRIPTIONS[displayPlan] || PLAN_DESCRIPTIONS.free}
                    </p>
                    {effectivePlan !== displayPlan && (
                        <p className="text-[11px] text-white/40 mt-2">
                            Effective tier: {PLAN_LABELS[effectivePlan] || effectivePlan}
                        </p>
                    )}
                    <ul className="mt-3 space-y-1.5" data-testid="billing-plan-highlights">
                        {(PLAN_HIGHLIGHTS[displayPlan] || PLAN_HIGHLIGHTS.free).map((line) => (
                            <li key={line} className="flex items-start gap-2 text-xs text-white/60">
                                <Check className="h-3.5 w-3.5 shrink-0 text-[#00BF0D] mt-px" aria-hidden />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                    <PlanCompareSection displayPlan={displayPlan} planPriceLabels={planPriceLabels} />
                </BillingCard>

                <BillingCard title="Usage & billing" testId="billing-usage-card" className="lg:h-full">
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-3 min-w-0" data-testid="billing-limits-section">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-white/35">Limits</p>
                                    {usageHasPressure && upgradeTargetPlan && (
                                        <p className="text-[11px] text-white/45 mt-1">
                                            Headroom is tight — upgrade to {PLAN_LABELS[upgradeTargetPlan]} for unlimited access.
                                        </p>
                                    )}
                                </div>
                                <UsageMeter
                                    icon={Users}
                                    label="Pool members"
                                    unitLabel="members"
                                    current={memberCount}
                                    limit={poolMemberLimit}
                                    displayPlan={displayPlan}
                                    upgradePlan={upgradeTargetPlan}
                                />
                                <UsageMeter
                                    icon={Layers}
                                    label="Pools"
                                    unitLabel="pools"
                                    current={poolCount}
                                    limit={poolLimit}
                                    displayPlan={displayPlan}
                                    upgradePlan={upgradeTargetPlan}
                                />
                            </div>

                            <div className="min-w-0" data-testid="billing-status-card">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-white/35 mb-3">
                                    Billing status
                                </p>
                                <dl className="space-y-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <dt className="flex items-center gap-1.5 text-white/50 text-xs">
                                            <Shield className="h-3.5 w-3.5" /> Status
                                        </dt>
                                        <dd className="text-white/80 text-xs capitalize">
                                            {cancelAtPeriodEnd ? "Active until period end" : (STATUS_LABELS[subscriptionStatus] || subscriptionStatus)}
                                        </dd>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 border-t border-white/[0.04] pt-2">
                                        <dt className="flex items-center gap-1.5 text-white/50 text-xs">
                                            <Calendar className="h-3.5 w-3.5" />
                                            {cancelAtPeriodEnd ? "Access until" : "Next billing"}
                                        </dt>
                                        <dd className="text-white/80 text-xs tabular-nums">
                                            {nextBillingDate || "—"}
                                        </dd>
                                    </div>
                                    {cancelAtPeriodEnd && (
                                        <div className="flex items-center justify-between gap-2 border-t border-white/[0.04] pt-2">
                                            <dt className="flex items-center gap-1.5 text-white/50 text-xs">
                                                <Calendar className="h-3.5 w-3.5" /> Renews
                                            </dt>
                                            <dd className="text-white/50 text-xs">No renewal</dd>
                                        </div>
                                    )}
                                </dl>
                                {(subscriptionStatus === "past_due" || (subscriptionStatus === "canceled" && displayPlan !== "free")) && (
                                    <div className="mt-3 space-y-2">{billingAlerts}</div>
                                )}
                            </div>
                        </div>

                        <IncludedFeaturesSection
                            effectivePlan={effectivePlan}
                            planFeatureRows={planFeatureRows}
                        />
                        <BillingQuickLinks effectivePlan={effectivePlan} />
                        <BillingRecentActivityPreview events={activityEvents} />
                    </div>
                </BillingCard>
            </div>

            <BillingCard
                id="billing-activity-full"
                title="Billing activity"
                subtitle="Full history of subscription checkouts and billing events"
                testId="billing-activity-card"
            >
                <BillingActivityTimeline events={activityEvents} />
            </BillingCard>
        </div>
    );
}

export function BillingPage() {
    const { user, activeOrg, refreshMe } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const { convert } = useFx();
    const [billing, setBilling] = useState(null);
    const [poolCount, setPoolCount] = useState(0);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [checkoutPlan, setCheckoutPlan] = useState(null);
    const [syncingPlan, setSyncingPlan] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
    const [checkoutSessionId, setCheckoutSessionId] = useState(null);
    const [checkoutPublishableKey, setCheckoutPublishableKey] = useState(null);
    const [checkoutInitError, setCheckoutInitError] = useState(null);
    const [canceling, setCanceling] = useState(false);
    const [resuming, setResuming] = useState(false);

    const orgId = activeOrg?.id;
    const canViewBilling = Boolean(user && orgId && ["owner", "admin"].includes(activeOrg?.role));
    const canManageBilling = Boolean(user && orgId && activeOrg?.role === "owner");

    const loadBilling = useCallback(async () => {
        if (!user || !orgId) {
            setBilling(null);
            setPoolCount(0);
            setAuditLogs([]);
            setError(null);
            setPermissionDenied(false);
            setLoading(false);
            return;
        }

        if (!canViewBilling) {
            setBilling(null);
            setPoolCount(0);
            setAuditLogs([]);
            setError(null);
            setPermissionDenied(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        setPermissionDenied(false);

        try {
            const [billingRes, poolsRes, auditRes] = await Promise.all([
                api.get("/billing/subscription", { params: { org_id: orgId } }),
                api.get("/pools", { params: { org_id: orgId } }).catch(() => ({ data: { pools: [] } })),
                api.get("/audit-logs", { params: { org_id: orgId, limit: 200 } }).catch(() => ({ data: { logs: [] } })),
            ]);
            setBilling(billingRes.data);
            setPoolCount((poolsRes.data?.pools || []).length);
            setAuditLogs(auditRes.data?.logs || []);
        } catch (e) {
            setBilling(null);
            setPoolCount(0);
            setAuditLogs([]);
            if (e.response?.status === 403) {
                setPermissionDenied(true);
                setError(null);
            } else {
                setError("Failed to load billing");
            }
        } finally {
            setLoading(false);
        }
    }, [user, orgId, canViewBilling]);

    useEffect(() => {
        loadBilling();
    }, [loadBilling]);

    const finalizePlanUpgrade = useCallback(async (message) => {
        await loadBilling();
        await refreshMe({ clearOnFailure: false, background: true });
        if (message) {
            toast.success(message);
        }
    }, [loadBilling, refreshMe]);

    const handleSyncFromStripe = useCallback(async () => {
        if (!orgId || syncingPlan || activeOrg?.role !== "owner") {
            return;
        }
        setSyncingPlan(true);
        try {
            const { data } = await api.post("/billing/subscription/sync", { org_id: orgId });
            if (data?.synced) {
                await finalizePlanUpgrade(`You're now on ${PLAN_LABELS[data.plan] || data.plan}`);
                return;
            }
            toast.info("No active subscription found in Stripe", {
                description: data?.reason === "no_stripe_customer"
                    ? "Complete checkout first, or wait a minute and try again."
                    : "If you just paid, wait a moment and try again.",
            });
            await loadBilling();
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not sync plan from Stripe"));
        } finally {
            setSyncingPlan(false);
        }
    }, [orgId, syncingPlan, activeOrg?.role, finalizePlanUpgrade, loadBilling]);

    useEffect(() => {
        if (!orgId || !canManageBilling) {
            return;
        }

        const billingReturn = searchParams.get("billing");
        if (!billingReturn) {
            return;
        }

        if (billingReturn === "canceled") {
            toast.info("Checkout canceled");
            setSearchParams({}, { replace: true });
            return;
        }

        if (billingReturn !== "success") {
            return;
        }

        const sessionId = searchParams.get("session_id");
        const planHint = searchParams.get("plan");
        setSearchParams({}, { replace: true });

        (async () => {
            let upgraded = false;

            if (sessionId) {
                try {
                    const confirmed = await confirmCheckoutWithRetry(orgId, sessionId);
                    if (confirmed) {
                        upgraded = true;
                        await finalizePlanUpgrade(
                            `You're now on ${PLAN_LABELS[confirmed.plan] || confirmed.plan || planHint}`,
                        );
                    }
                } catch (_e) {
                    // Confirm can fail while Stripe is still finalizing; fall through to sync.
                }
            }

            if (!upgraded) {
                try {
                    const synced = await syncSubscriptionWithRetry(orgId, 10, 2000);
                    if (synced) {
                        upgraded = true;
                        await finalizePlanUpgrade(
                            `You're now on ${PLAN_LABELS[synced.plan] || synced.plan || planHint}`,
                        );
                    }
                } catch (syncError) {
                    toast.error(extractAxiosError(syncError, "Could not confirm subscription upgrade"));
                    await loadBilling();
                    return;
                }
            }

            if (!upgraded) {
                toast.info("Payment received — plan sync pending", {
                    description: 'Your upgrade should appear shortly. Tap "Refresh from Stripe" if it does not.',
                });
                await loadBilling();
            }
        })();
    }, [
        orgId,
        canManageBilling,
        searchParams,
        setSearchParams,
        finalizePlanUpgrade,
        loadBilling,
    ]);

    const startCheckout = useCallback(
        async (plan, { mode = "embedded" } = {}) => {
            const origin = `${window.location.origin}/billing`;
            return api.post("/billing/checkout", {
                org_id: orgId,
                plan,
                origin_url: origin,
                checkout_mode: mode,
            });
        },
        [orgId],
    );

    const handleRedirectCheckout = useCallback(
        async (plan) => {
            if (!orgId || !plan) {
                return;
            }
            try {
                const { data } = await startCheckout(plan, { mode: "redirect" });
                if (data?.checkout_url) {
                    window.location.assign(data.checkout_url);
                    return;
                }
                toast.error("Redirect checkout is unavailable", {
                    description: "Stripe did not return a checkout URL.",
                });
            } catch (e) {
                toast.error(extractAxiosError(e, "Could not open Stripe checkout"));
            }
        },
        [orgId, startCheckout],
    );

    const handleUpgrade = useCallback(async (plan) => {
        if (!orgId || checkoutPlan || activeOrg?.role !== "owner") {
            if (activeOrg?.role !== "owner") {
                toast.error("Only the workspace owner can change billing plan");
            }
            return;
        }
        setCheckoutPlan(plan);
        setCheckoutInitError(null);
        try {
            const { data } = await startCheckout(plan, { mode: "embedded" });

            if (data?.configured === false) {
                toast.info("Stripe checkout is not configured yet", {
                    description: data.todo || "Enable STRIPE_API_KEY on the server to go live.",
                });
                setCheckoutPlan(null);
                return;
            }

            const publishableKey = resolveStripePublishableKey({ checkoutData: data, billing });
            const modeMismatch = stripeKeyModeMismatchMessage({
                publishableKey,
                serverMode: data?.stripe_mode,
            });

            if (data?.client_secret) {
                if (!publishableKey) {
                    toast.error("In-app checkout is not configured", {
                        description:
                            "Add STRIPE_PUBLISHABLE_KEY on the backend (Render), or REACT_APP_STRIPE_PUBLISHABLE_KEY on the frontend (Netlify), then redeploy.",
                    });
                    setCheckoutPlan(null);
                    return;
                }
                if (modeMismatch) {
                    setCheckoutPublishableKey(publishableKey);
                    setCheckoutClientSecret(data.client_secret);
                    setCheckoutSessionId(data.session_id);
                    setCheckoutInitError(modeMismatch);
                    setCheckoutOpen(true);
                    return;
                }
                setCheckoutPublishableKey(publishableKey);
                setCheckoutClientSecret(data.client_secret);
                setCheckoutSessionId(data.session_id);
                setCheckoutInitError(null);
                setCheckoutOpen(true);
                return;
            }

            if (data?.checkout_url) {
                window.location.assign(data.checkout_url);
                return;
            }

            toast.info("Checkout session could not be started", {
                description: "The billing API responded without checkout credentials.",
            });
            setCheckoutPlan(null);
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not start checkout"));
            setCheckoutPlan(null);
        }
    }, [orgId, checkoutPlan, activeOrg?.role, billing, startCheckout]);

    const handleCheckoutComplete = useCallback(async () => {
        setCheckoutOpen(false);
        const sessionId = checkoutSessionId;
        const planHint = checkoutPlan;
        setCheckoutClientSecret(null);
        setCheckoutSessionId(null);
        setCheckoutPlan(null);

        let upgraded = false;
        if (sessionId) {
            try {
                const confirmed = await confirmCheckoutWithRetry(orgId, sessionId);
                if (confirmed) {
                    upgraded = true;
                    await finalizePlanUpgrade(
                        `You're now on ${PLAN_LABELS[confirmed.plan] || confirmed.plan || planHint}`,
                    );
                }
            } catch (_e) {
                // Fall through to sync while Stripe finalizes.
            }
        }

        if (!upgraded) {
            try {
                const synced = await syncSubscriptionWithRetry(orgId, 10, 2000);
                if (synced) {
                    await finalizePlanUpgrade(
                        `You're now on ${PLAN_LABELS[synced.plan] || synced.plan || planHint}`,
                    );
                    return;
                }
                toast.info("Payment received — plan sync pending", {
                    description: 'Your upgrade should appear shortly. Tap "Refresh from Stripe" if it does not.',
                });
                await loadBilling();
            } catch (e) {
                toast.error(extractAxiosError(e, "Could not confirm subscription upgrade"));
                await loadBilling();
            }
        }
    }, [checkoutSessionId, checkoutPlan, orgId, finalizePlanUpgrade, loadBilling]);

    const handleCancelSubscription = useCallback(async () => {
        if (!orgId || canceling) {
            return;
        }
        setCanceling(true);
        try {
            const { data } = await api.post("/billing/subscription/cancel", { org_id: orgId });
            if (data?.canceled) {
                const endDate = formatBillingDate(data.current_period_end);
                toast.success(
                    endDate
                        ? `Subscription canceled — ${PLAN_LABELS[billing?.plan] || billing?.plan || "paid"} access continues until ${endDate}`
                        : "Subscription canceled — access continues until the end of your billing period",
                );
                await loadBilling();
                return;
            }
            toast.info("No active subscription to cancel");
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not cancel subscription"));
        } finally {
            setCanceling(false);
        }
    }, [orgId, canceling, loadBilling, billing?.plan]);

    const handleResumeSubscription = useCallback(async () => {
        if (!orgId || resuming) {
            return;
        }
        setResuming(true);
        try {
            const { data } = await api.post("/billing/subscription/resume", { org_id: orgId });
            if (data?.resumed) {
                toast.success("Subscription renewed — your plan will continue");
                await loadBilling();
                return;
            }
            toast.info("No subscription to resume");
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not resume subscription"));
        } finally {
            setResuming(false);
        }
    }, [orgId, resuming, loadBilling]);

    const effectivePlan = billing?.effective_plan ?? billing?.plan ?? "free";
    const displayPlan = billing?.plan ?? "free";
    const subscriptionStatus = billing?.subscription_status ?? "active";
    const cancelAtPeriodEnd = Boolean(billing?.cancel_at_period_end);
    const memberCount = billing?.usage?.pool_members ?? billing?.usage?.members ?? 0;
    const poolMemberLimit = billing?.limits?.max_pool_members ?? billing?.limits?.max_users ?? (effectivePlan === "free" ? 3 : null);
    const poolLimit = billing?.limits?.max_pools ?? poolLimitForPlan(effectivePlan);
    const nextBillingDate = formatBillingDate(
        billing?.current_period_end ?? billing?.next_billing_date ?? null,
    );
    const monthlyPriceLabel = resolveMonthlyPriceLabel(billing, displayPlan);

    const featureFlags = useMemo(
        () => resolveFeatureFlags(billing, effectivePlan),
        [billing, effectivePlan],
    );

    const planFeatureRows = useMemo(() => {
        const rows = featuresForPlanDisplay(effectivePlan);
        return rows.map((f) => ({
            ...f,
            enabled: isFeatureEnabled(f.key, featureFlags, effectivePlan),
        }));
    }, [effectivePlan, featureFlags]);

    const showUpgradeFree = displayPlan === "free" && canManageBilling;
    const showUpgradeBusiness = displayPlan === "pro" && canManageBilling;
    const showBillingGrid = user && orgId && canViewBilling && !error && !permissionDenied;

    const activityEvents = useMemo(
        () => buildBillingActivityEvents(auditLogs, billing),
        [auditLogs, billing],
    );

    const displayCurrency = (user?.display_currency || DEFAULT_CURRENCY).toUpperCase();
    const planPriceLabels = useMemo(() => ({
        pro: formatUpgradePlanPrice(billing?.plan_prices, "pro", displayCurrency, convert),
        business: formatUpgradePlanPrice(billing?.plan_prices, "business", displayCurrency, convert),
    }), [billing?.plan_prices, displayCurrency, convert]);

    const placeholderBilling = { stripe_subscription_id: null };

    return (
        <PageShell flow>
            <div className="mx-auto w-full max-w-6xl space-y-3 sm:space-y-4">
                <header className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <div>
                        <p className="label-eyebrow">Subscription</p>
                        <h1 className="font-heading text-2xl tracking-tight mt-0.5">Billing</h1>
                    </div>
                    <div className="flex flex-col items-start gap-2 sm:items-end">
                        <p className="text-xs text-white/45 sm:text-right">
                            {activeOrg?.name || "Your workspace"}
                        </p>
                    </div>
                </header>

                {!user && (
                    <div className="surface p-5 text-sm text-white/55" data-testid="billing-no-user">
                        Sign in to view billing for your workspace.
                    </div>
                )}

                {user && !orgId && (
                    <div className="surface p-5 text-sm text-white/55" data-testid="billing-no-org">
                        No workspace selected. Join or create an organisation to manage billing.
                    </div>
                )}

                {user && orgId && !loading && permissionDenied && (
                    <div className="surface p-5 text-sm text-white/55" data-testid="billing-permission-denied">
                        Only workspace owners and admins can view billing settings.
                    </div>
                )}

                {user && orgId && !loading && error && (
                    <div className="surface p-5 text-sm text-rose-300" data-testid="billing-error">
                        {error}
                    </div>
                )}

                {showBillingGrid && !canManageBilling && (
                    <p className="text-xs text-white/45 -mt-1" data-testid="billing-owner-only-note">
                        Billing is read-only. Contact the workspace owner to upgrade plan or manage subscription.
                    </p>
                )}

                {showBillingGrid && (
                    <div className="relative min-h-[24rem]">
                        <div className={loading ? "pointer-events-none invisible" : ""} aria-hidden={loading}>
                            <BillingDashboard
                                billing={billing || placeholderBilling}
                                displayPlan={displayPlan}
                                effectivePlan={effectivePlan}
                                subscriptionStatus={subscriptionStatus}
                                memberCount={memberCount}
                                poolMemberLimit={poolMemberLimit}
                                poolCount={poolCount}
                                poolLimit={poolLimit}
                                nextBillingDate={nextBillingDate}
                                monthlyPriceLabel={monthlyPriceLabel}
                                planFeatureRows={planFeatureRows}
                                showUpgradeFree={showUpgradeFree}
                                showUpgradeBusiness={showUpgradeBusiness}
                                checkoutPlan={checkoutPlan}
                                onUpgrade={handleUpgrade}
                                activityEvents={activityEvents}
                                planPriceLabels={planPriceLabels}
                                onSyncFromStripe={handleSyncFromStripe}
                                syncingPlan={syncingPlan}
                                cancelAtPeriodEnd={cancelAtPeriodEnd}
                                canManageBilling={canManageBilling}
                                canceling={canceling}
                                resuming={resuming}
                                onCancelSubscription={handleCancelSubscription}
                                onResumeSubscription={handleResumeSubscription}
                            />
                        </div>
                        {loading && (
                            <div className="absolute inset-0 grid place-items-center">
                                <LoadingIndicator label="Loading billing..." testId="billing-loading" />
                            </div>
                        )}
                    </div>
                )}
                {showBillingGrid && (
                    <SupportContactStrip variant="billing" className="mt-2" />
                )}

                <BillingCheckoutModal
                    open={checkoutOpen}
                    onOpenChange={(open) => {
                        setCheckoutOpen(open);
                        if (!open) {
                            setCheckoutClientSecret(null);
                            setCheckoutSessionId(null);
                            setCheckoutPublishableKey(null);
                            setCheckoutInitError(null);
                            setCheckoutPlan(null);
                        }
                    }}
                    publishableKey={checkoutPublishableKey}
                    clientSecret={checkoutClientSecret}
                    plan={checkoutPlan}
                    initError={checkoutInitError}
                    onComplete={handleCheckoutComplete}
                    onRedirectFallback={
                        checkoutPlan ? () => handleRedirectCheckout(checkoutPlan) : undefined
                    }
                />
            </div>
        </PageShell>
    );
}
